function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function createLine(offset) {
  return {
    id: `line_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    offset,
  };
}

function makeSplitLines(count, total) {
  const n = toNumber(count, 0);
  const size = toNumber(total, 0);

  if (n <= 0 || size <= 0) return [];

  if (n === 1) {
    return [createLine(size)];
  }

  return Array.from({ length: n - 1 }, (_, index) =>
    createLine(Math.round((size / n) * (index + 1)))
  );
}

function getTargetNode({
  action,
  selectedNode,
  simpleSelectedNode,
}) {
  if (action.target === "selected") {
    return selectedNode || simpleSelectedNode;
  }

  if (action.target === "simpleSelected") {
    return simpleSelectedNode || selectedNode;
  }

  return simpleSelectedNode || selectedNode;
}

function buildFrontPartitionsFromAction(action, resolvedNode) {
  const effective = resolvedNode?.effectiveParameters || {};
  const width = toNumber(effective.width, 600);
  const height = toNumber(effective.height, 720);

  const frontType = String(action.frontType || "").toLowerCase();

  if (frontType === "none" || frontType === "open") {
    return {
      front: {
        horizontal: [],
        vertical: [],
        panelOverrides: {},
      },
    };
  }

  if (frontType === "drawers") {
    const drawerCount = toNumber(action.drawerCount, 3);

    return {
      front: {
        horizontal: makeSplitLines(drawerCount, height),
        vertical: [],
        panelOverrides: {},
      },
    };
  }

  if (frontType === "doors") {
    const doorCount = toNumber(action.doorCount, width >= 700 ? 2 : 1);

    return {
      front: {
        horizontal: [],
        vertical: makeSplitLines(doorCount, width),
        panelOverrides: {},
      },
    };
  }

  return undefined;
}

function mergeParameterOverrides(node, patch = {}) {
  return {
    ...(node?.parameterOverrides || {}),
    ...(patch || {}),
  };
}

function flattenNodes(tree = [], acc = []) {
  (tree || []).forEach((node) => {
    acc.push(node);
    flattenNodes(node.children || [], acc);
  });

  return acc;
}

function getCategory(node) {
  return String(
    node?.category ||
      node?.definition?.category ||
      ""
  ).toLowerCase();
}

function matchesFilter(node, filter = {}) {
  const category = getCategory(node);
  const name = String(node?.name || "").toLowerCase();

  const cabinetType = String(filter.cabinetType || "any").toLowerCase();

  const isCabinet = category.includes("cabinet");

  if (!isCabinet) return false;

  if (cabinetType === "any") return true;

  if (cabinetType === "wall") {
    return (
      category.includes("cabinet.wall") ||
      category.includes("wall") ||
      name.includes("wall")
    );
  }

  if (cabinetType === "base") {
    return (
      category.includes("cabinet.base") ||
      category.includes("cabinet.box") ||
      category.includes("base") ||
      name.includes("base") ||
      name.includes("cabinet box")
    );
  }

  if (cabinetType === "tall") {
    return (
      category.includes("cabinet.tall") ||
      category.includes("tall") ||
      name.includes("tall") ||
      name.includes("fridge")
    );
  }

  return false;
}

function getNodeKey(node) {
  return node?.id ? String(node.id) : "";
}

export function executeSceneEditActions({
  editResult,

  selectedNode,
  resolvedNode,
  simpleSelectedNode,
  simpleResolvedNode,
  resolvedNodes = [],

  applyNodePatch,
  applyProjectParametersChange,
  handleQuickAdd,
  removeSelectedNode,
}) {
  const actions = editResult?.actions || [];
  const applied = [];

  const parameterCache = new Map();

function getCachedParameterOverrides(node) {
  const key = getNodeKey(node);

  if (!key) return node?.parameterOverrides || {};

  if (!parameterCache.has(key)) {
    parameterCache.set(key, {
      ...(node?.parameterOverrides || {}),
    });
  }

  return parameterCache.get(key);
}

function mergeCachedParameterOverrides(node, patch = {}) {
  const key = getNodeKey(node);
  if (!key) return patch || {};

  const current = getCachedParameterOverrides(node);

  const next = {
    ...(current || {}),
    ...(patch || {}),
  };

  parameterCache.set(key, next);

  return next;
}

  actions.forEach((action) => {
    if (!action?.type) return;

    if (action.type === "set_project_material") {
      if (!action.key || !action.materialId) return;

      applyProjectParametersChange?.((prev) => ({
        ...(prev || {}),
        [action.key]: action.materialId,
      }));

      applied.push(action);
      return;
    }

    if (action.type === "update_node_parameters") {
  const targetNode =
    action.target === "selected"
      ? selectedNode || simpleSelectedNode
      : simpleSelectedNode || selectedNode;

  if (!targetNode?.id) return;

  applyNodePatch?.({
    id: targetNode.id,
    parameterOverrides: mergeCachedParameterOverrides(
      targetNode,
      action.parameterOverrides
    ),
  });

  applied.push(action);
  return;
}

    if (action.type === "update_nodes_by_filter") {
  const allResolvedNodes = flattenNodes(resolvedNodes);
  const matchedNodes = allResolvedNodes.filter((node) =>
    matchesFilter(node, action.filter || {})
  );

  matchedNodes.forEach((node) => {
    applyNodePatch?.({
      id: node.id,
      parameterOverrides: {
        ...(node.parameterOverrides || {}),
        ...(action.parameterOverrides || {}),
      },
    });
  });

  if (matchedNodes.length) {
    applied.push({
      ...action,
      matchedCount: matchedNodes.length,
    });
  }

  return;
}

    if (action.type === "update_node_transform") {
      const targetNode = getTargetNode({
        action,
        selectedNode,
        simpleSelectedNode,
      });

      if (!targetNode?.id || !action.transform) return;

      applyNodePatch?.({
        id: targetNode.id,
        transform: {
          ...(targetNode.transform || {}),
          ...(action.transform || {}),
        },
      });

      applied.push(action);
      return;
    }

    if (action.type === "set_front_layout") {
      const targetNode = getTargetNode({
        action,
        selectedNode,
        simpleSelectedNode,
      });

      const targetResolved =
        action.target === "selected"
          ? resolvedNode || simpleResolvedNode
          : simpleResolvedNode || resolvedNode;

      if (!targetNode?.id) return;

      const partitions = buildFrontPartitionsFromAction(action, targetResolved);

      const parameterPatch = {};

      if (action.frontType === "drawers") {
        parameterPatch.drawerCount = toNumber(action.drawerCount, 3);
      }

      if (action.frontType === "doors") {
        parameterPatch.doorCount = toNumber(action.doorCount, 1);
      }

      if (action.frontType) {
        parameterPatch.frontType = action.frontType;
      }

      applyNodePatch?.({
        id: targetNode.id,
        parameterOverrides: mergeCachedParameterOverrides(targetNode, parameterPatch),
        ...(partitions ? { partitions } : {}),
      });

      applied.push(action);
      return;
    }

    if (action.type === "set_cabinet_role") {
      const targetNode = getTargetNode({
        action,
        selectedNode,
        simpleSelectedNode,
      });

      if (!targetNode?.id) return;

      const parameterPatch = {
        cabinetRole: action.cabinetRole || "standard",
        applianceType: action.applianceType || "none",
      };

      if (action.frontType) {
        parameterPatch.frontType = action.frontType;
      }

      applyNodePatch?.({
        id: targetNode.id,
        parameterOverrides: mergeCachedParameterOverrides(targetNode, parameterPatch),
      });

      if (action.frontType) {
        const targetResolved = simpleResolvedNode || resolvedNode;
        const partitions = buildFrontPartitionsFromAction(action, targetResolved);

        if (partitions) {
          applyNodePatch?.({
            id: targetNode.id,
            partitions,
          });
        }
      }

      applied.push(action);
      return;
    }

    if (action.type === "add_cabinet_next_to_selected") {
      handleQuickAdd?.(action.direction || "right");
      applied.push(action);
      return;
    }

    if (action.type === "delete_selected") {
      removeSelectedNode?.();
      applied.push(action);
      return;
    }
  });

  return {
    appliedCount: applied.length,
    applied,
  };
}